import * as THREE from 'three';
import { DigitalObject } from '../../rendering/binary/DigitalObject.js';

export const INTERIOR_DOOR_WIDTH = 1.65;
export const INTERIOR_WALL_THICKNESS = 0.35;

/**
 * @typedef {{
 *   group: THREE.Group;
 *   buildingMaterial: import('../../rendering/binary/BinaryMaterial.js').BinaryMaterial;
 *   accentMaterial: import('../../rendering/binary/BinaryMaterial.js').BinaryMaterial;
 *   seed: number;
 *   cellIdBase: number;
 *   w: number;
 *   d: number;
 *   h: number;
 *   colliders: import('../Colliders.js').BoxCollider[];
 *   signs: import('../city/DigitalSign.js').DigitalSign[];
 * }} InteriorBuildContext
 */

/**
 * @param {InteriorBuildContext} ctx
 */
export function buildFloor(ctx) {
  const { w, d, buildingMaterial, seed, group } = ctx;
  const floor = new DigitalObject({
    material: buildingMaterial,
    shape: 'box',
    size: [w, 0.12, d],
    position: [0, 0.06, 0],
    cellsPerMeter: 6,
    seed: seed + 1,
    cellIdBase: ctx.cellIdBase,
    name: 'InteriorFloor',
  });
  ctx.cellIdBase += floor.cellsUsed;
  group.add(floor.group);
  pushBoxCollider(ctx, 0, 0.06, 0, w, 0.12, d, true);
}

/**
 * @param {InteriorBuildContext} ctx
 */
export function buildCeiling(ctx) {
  const { w, d, h, buildingMaterial, seed, group } = ctx;
  const ceiling = new DigitalObject({
    material: buildingMaterial,
    shape: 'box',
    size: [w - 0.2, 0.14, d - 0.2],
    position: [0, h - 0.08, 0],
    cellsPerMeter: 5,
    seed: seed + 2,
    cellIdBase: ctx.cellIdBase,
    name: 'InteriorCeiling',
  });
  ctx.cellIdBase += ceiling.cellsUsed;
  group.add(ceiling.group);
}

/**
 * @param {InteriorBuildContext} ctx
 */
export function buildPerimeterWalls(ctx) {
  const { w, d, h, buildingMaterial, seed } = ctx;
  const wallT = INTERIOR_WALL_THICKNESS;
  const doorW = INTERIOR_DOOR_WIDTH;

  addSolidWallSegment(ctx, buildingMaterial, seed + 10, -d / 2, w, h, wallT, 'south');
  addDoorWall(ctx, buildingMaterial, seed + 20, d / 2, w, h, wallT, 'north', doorW);
  addSolidWallSegment(ctx, buildingMaterial, seed + 30, 0, d, h, wallT, 'west', -w / 2);
  addSolidWallSegment(ctx, buildingMaterial, seed + 40, 0, d, h, wallT, 'east', w / 2);
}

/**
 * @param {InteriorBuildContext} ctx
 * @param {number} cx
 * @param {number} cz
 * @param {number} sx
 * @param {number} sy
 * @param {number} sz
 */
export function pushBoxCollider(ctx, cx, cy, cz, sx, sy, sz, isFloor = false) {
  ctx.colliders.push({
    min: new THREE.Vector3(cx - sx / 2, cy - sy / 2, cz - sz / 2),
    max: new THREE.Vector3(cx + sx / 2, cy + sy / 2, cz + sz / 2),
    isFloor,
  });
}

/**
 * @param {InteriorBuildContext} ctx
 * @param {{ x: number; y: number; z: number }} spawnLocal
 * @param {{ x: number; y: number; z: number }} origin
 */
export function resolveSpawnExit(ctx, spawnLocal, origin) {
  const spawnY = origin.y + 0.02;
  return {
    spawnPosition: new THREE.Vector3(
      origin.x + spawnLocal.x,
      spawnY,
      origin.z + spawnLocal.z,
    ),
    exitPosition: new THREE.Vector3(
      origin.x,
      spawnY,
      origin.z + ctx.d / 2 - 0.65,
    ),
  };
}

function addDoorWall(ctx, material, seed, zCenter, span, height, thickness, name, doorW) {
  const seg = (span - doorW) / 2;
  if (seg < 0.4) return;
  for (const side of [-1, 1]) {
    const cx = side * (doorW / 2 + seg / 2);
    addWallMesh(ctx, material, seed + side, cx, height / 2, zCenter, seg, height, thickness, `${name}_${side}`);
    pushBoxCollider(ctx, cx, height / 2, zCenter, seg, height, thickness);
  }
}

function addSolidWallSegment(
  ctx,
  material,
  seed,
  zCenter,
  span,
  height,
  thickness,
  name,
  xCenter = 0,
) {
  const isXWall = name === 'west' || name === 'east';
  const sx = isXWall ? thickness : span;
  const sy = height;
  const sz = isXWall ? span : thickness;
  const cx = isXWall ? xCenter : 0;
  const cz = isXWall ? 0 : zCenter;
  addWallMesh(ctx, material, seed, cx, height / 2, cz, sx, sy, sz, name);
  pushBoxCollider(ctx, cx, height / 2, cz, sx, sy, sz);
}

function addWallMesh(ctx, material, seed, x, y, z, sx, sy, sz, name) {
  const wall = new DigitalObject({
    material,
    shape: 'wall',
    size: [sx, sy, sz],
    position: [x, y, z],
    cellsPerMeter: 6,
    seed,
    cellIdBase: ctx.cellIdBase,
    name: `Wall_${name}`,
  });
  ctx.cellIdBase += wall.cellsUsed;
  ctx.group.add(wall.group);
}
